import { useEffect, useState } from 'react';

import { useYearQuarterNavigation } from 'hooks';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useRecoilValue } from 'recoil';

import { displayNumber, displayPercentage } from 'utils/misc';

import {
  EmployerSettingsResponse,
  getEmployerSettings,
  PutEmployerSettingsPayload,
  saveEmployerSettings
} from 'RestAPIs/employer-settings';

import { scenarioAtom } from '../../atoms';

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

import { LoadingOverlay, showSnackbar } from 'components/shared';
import { ZebraStripedTableRow } from 'components/shared/Table';

import LeaveTypeSelect from './components/LeaveTypeSelect';

import { getCalculatedHoursFromNPH } from './util';

export type CalculatedHours = {
  plb: number;
  leave: number;
  illness: number;
  festiveDays: number;
  netPerFTE: number;
};

const editableHours = ['illness', 'plb'];

const GrossNetHoursCalculation = () => {
  const { t } = useTranslation('common');
  const { year } = useYearQuarterNavigation();
  const scenario = useRecoilValue(scenarioAtom);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [employerSettings, setEmployerSettings] =
    useState<EmployerSettingsResponse | null>(null);
  const [dataForSave, setDataForSave] = useState<PutEmployerSettingsPayload>({
    nphPercentageSubstitute: {
      illness: null,
      plb: null
    }
  });
  const [calculatedHours, setCalculatedHours] =
    useState<CalculatedHours | null>(null);

  const refreshScreen = () => {
    if (scenario?.scenarioId) {
      setIsLoading(true);

      getEmployerSettings(scenario.scenarioId, year)
        .then(({ data }) => {
          setEmployerSettings(data);
          setCalculatedHours(getCalculatedHoursFromNPH(data));
          setDataForSave({
            nphPercentageSubstitute: {
              illness: data.nphPercentageSubstitute.illness ?? null,
              plb: data.nphPercentageSubstitute.plb ?? null
            }
          });
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    refreshScreen();
  }, [scenario, year]);

  const onBlurInput = ({ type, value }) => {
    setDataForSave({
      nphPercentageSubstitute: {
        ...dataForSave.nphPercentageSubstitute,
        [type]: parseFloat(value.replace(',', '.')) / 100 ?? null
      }
    });
  };

  const onClickSave = () => {
    if (!scenario) {
      return;
    }

    saveEmployerSettings(dataForSave, scenario.scenarioId, year)
      .then(() => {
        showSnackbar({
          severity: 'success',
          message: t('nonProductiveHours.savedSuccessfully')
        });
        refreshScreen();
      })
      .catch(() => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      });
  };

  const onClickCancel = () => {
    setDataForSave({
      nphPercentageSubstitute: {
        illness: employerSettings?.nphPercentageSubstitute.illness ?? null,
        plb: employerSettings?.nphPercentageSubstitute.plb ?? null
      }
    });
  };

  const getCellContentForRowType = (type: string) => {
    if (editableHours.includes(type)) {
      return (
        <NumericFormat
          customInput={TextField}
          decimalSeparator=","
          onBlur={(e) => onBlurInput({ type, value: e.target.value })}
          type="text"
          value={
            dataForSave.nphPercentageSubstitute[type]
              ? displayNumber(dataForSave.nphPercentageSubstitute[type] * 100)
              : ''
          }
          valueIsNumericString
          disabled={scenario?.readOnly}
          suffix="%"
        />
      );
    }
    return displayPercentage(employerSettings?.nphPercentage[type]) ?? '';
  };

  return (
    <Box mt={2}>
      <LoadingOverlay loading={isLoading} />
      <Grid container>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('selectLeaveTypeForScenario')}
          </Typography>
          <LeaveTypeSelect />
        </Grid>
      </Grid>
      {!scenario?.readOnly && (
        <Grid container mt={4}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('substituteEmployerSettingsForScenario')}
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('description')}</TableCell>
                    <TableCell>{t('employerSettings')}</TableCell>
                    <TableCell>{t('substituteWith')}</TableCell>
                    <TableCell>{t('hours')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && employerSettings && calculatedHours && (
                    <>
                      <ZebraStripedTableRow>
                        <TableCell colSpan={3}>
                          {t('grossYearHoursCLA')}
                        </TableCell>
                        <TableCell>
                          {displayNumber(employerSettings.yearHours)}
                        </TableCell>
                      </ZebraStripedTableRow>
                      {Object.keys(employerSettings.nphPercentage).map((k) => (
                        <ZebraStripedTableRow key={`nph_settings_${k}`}>
                          <TableCell>{t(`nonProductiveHours.${k}`)}</TableCell>
                          <TableCell>
                            {displayPercentage(
                              employerSettings.nphPercentage[k]
                            )}
                          </TableCell>
                          <TableCell>{getCellContentForRowType(k)}</TableCell>
                          <TableCell>
                            {displayNumber(calculatedHours[k])}
                          </TableCell>
                        </ZebraStripedTableRow>
                      ))}
                      <ZebraStripedTableRow>
                        <TableCell colSpan={3}>{t('netHoursPerFTE')}</TableCell>
                        <TableCell>
                          {displayNumber(calculatedHours.netPerFTE)}
                        </TableCell>
                      </ZebraStripedTableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!scenario?.readOnly && (
              <Box py={3} display="flex" justifyContent="end">
                <Button
                  sx={{ marginRight: 2 }}
                  variant="outlined"
                  onClick={onClickCancel}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onClickSave}
                >
                  {t('save')}
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default GrossNetHoursCalculation;
